<script setup lang="ts">
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { api } from '@/api';
import {
  MedicalSystemRtSystemDto,
  QaEquipmentRtSystemDto,
  TaskItemAreaDto,
  TaskItemDto,
  CopyTaskItemAreaDto,
  CopyTaskItemAreaDtoTypeEnum,
  ReferencingObjectDto,
  EvaluationParameterScopeDtoTypeEnum,
} from '@/api/generator';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import Tabs from '@/components/Tabs/Tabs.vue';
import { useTabs } from '@/components/Tabs/useTabs.ts';
import { mdiMinus, mdiPlus } from '@mdi/js';
import { duplicate } from '@/components/icons/icons';
import { NIL } from 'uuid';
import { useFieldArray, useFormValues, FieldEntry } from 'vee-validate';
import AreaForm from './AreaForm.vue';
import { usesManualEvaluations } from './setupParameterRules';
import { getTaskItemAreaDataTemplate } from './taskItemAreaData';
import { t } from 'i18next';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { toCreateOrUpdateTaskItemAreaDto } from '../mapping.ts';
import EntityScopeSelectionModal from './EntityScopeSelectionModal.vue';
import { useDeleteTaskAreaWithConfirmation } from '../utils.ts';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults.ts';
import { EntityInfo } from '@/components/confirmationModals/types.ts';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import { NotificationMessage } from '@/components/notifications/types.ts';
import { useFormTab } from '@/components/forms/useFormTab.ts';

const medicalSystems = ref<MedicalSystemRtSystemDto[]>([]);
const qaEquipmentSystemEntities = ref<QaEquipmentRtSystemDto[]>([]);

const areas = useFieldArray<TaskItemAreaDto>('taskItemAreas');
const { action: removeTaskTargetAreaAction } = useDeleteTaskAreaWithConfirmation(areas);
const taskItem = useFormValues<TaskItemDto>();
const mode = useFormMode();
const entityReferences = ref<ReferencingObjectDto[] | null>(null);
const duplicatedAreaIndices = ref<number[]>([]);

const { getIndicatorState, handleTabChange } = useFormTab();

async function getSystemEntities() {
  const { result: medicalSystemsResult } = await api.taskItemsApi.getMedicalSystemsForTask({
    getMedicalSystemsForTaskRequestDto: {
      taskType: taskItem.value.taskType || 'BeamConstancyCheck',
      category: taskItem.value.category || 'TreatmentMachine',
      subCategory: taskItem.value.subCategory || 'Brachytherapy',
    },
  });

  if (medicalSystemsResult) {
    medicalSystems.value = medicalSystemsResult;
  }

  const { result: qaEquipmentSystemEntitiesResult } =
    await api.qaEquipmentApi.getQaEquipmentRtSystemList();

  if (qaEquipmentSystemEntitiesResult?.value) {
    qaEquipmentSystemEntities.value = qaEquipmentSystemEntitiesResult.value;
  }
}

function getTabTitle(area: FieldEntry<TaskItemAreaDto>) {
  return computed(() => {
    const targetSystem = medicalSystems.value.find(
      (medicalSystem) => medicalSystem.id === area.value.medicalSystemSetup?.targetSystemEntityId,
    );

    return targetSystem?.name ?? t('TargetArea_one');
  });
}

const tabs = computed(() => {
  return Object.fromEntries(
    areas.fields.value.map((area, index) => [
      index,
      {
        title: getTabTitle(area),
        contextMenuActions:
          mode.value !== 'details' ? getAreaContextMenuActions(index, area.value) : undefined,
        indicatorState: getIndicatorState([`taskItemAreas[${index}]`]),
        area: area.value,
      },
    ]),
  );
});

const {
  activeIndex: selectedAreaIndex,
  activeTabDefinition,
  selectLast,
  selectIndex,
} = useTabs({ tabs });

let lastAddedAreaIndex: number | null = 0;

async function changeTab(index: number) {
  selectIndex(index);
}

// `setTouched` and `validate` are triggered, when <AreaForm> has mounted successfully
watch(selectedAreaIndex, async () => {
  if (selectedAreaIndex.value !== lastAddedAreaIndex) {
    await nextTick(); // mounting <AreaForm />
    await nextTick(); // mounting VeeForm components with `useField` inside <AreaForm />
    lastAddedAreaIndex = null;
    handleTabChange();
  }
});

const selectedArea = computed(() => activeTabDefinition.value?.area);

function getAreaContextMenuActions(index: number, area: TaskItemAreaDto): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [];

  if (areas.fields.value.length > 1) {
    const removeAction = {
      text: t('Remove'),
      icon: mdiMinus,
      callback: () => {
        if (areas.fields.value[index].value.isReferenced) {
          const confOptions = getDefaultDeleteConfirmationOptions(
            {
              ...(areas.fields.value[index].value as EntityInfo),
              typeName: 'TargetArea_one',
            },
            t,
          );
          removeTaskTargetAreaAction({
            param: index,
            confirmationOptions: {
              ...confOptions,
              size: 'large',
              contentData: confOptions.contentData && {
                ...confOptions.contentData,
                hint: `${t('TaskRemoveTargetAreaInfoMsg', { effects: t('TaskTargetAreaChangeEffects') })}\n\n ${confOptions.contentData.hint}`,
              },
            },
          });
        } else {
          duplicatedAreaIndices.value = duplicatedAreaIndices.value
            .filter((i) => i !== index)
            .map((i) => (i > index ? i - 1 : i));
          areas.remove(index);
        }
      },
    };
    actions.push(removeAction);
  }

  const isMedicalSystemSelected =
    area.medicalSystemSetup?.targetSystemEntityId &&
    (medicalSystems.value?.length || 0) > areas.fields.value.length;

  if (isMedicalSystemSelected) {
    actions.push({
      text: t('Copy'),
      icon: duplicate,
      callback: () => prepareDuplicate(index),
    });
  }

  return actions;
}

async function addNewArea() {
  const newArea: TaskItemAreaDto = {
    id: NIL,
    qaEquipmentSetups: [],
    evaluationScopes: usesManualEvaluations(taskItem.value.taskType)
      ? [
          {
            id: NIL,
            isEnabled: true,
            evaluationParameters: [],
            $type:
              EvaluationParameterScopeDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoEvaluationParametersEvaluationParameterScopeDtoPtwPtw100AppZeroApplicationShared,
          },
        ]
      : [],
    taskTypeData: await getTaskItemAreaDataTemplate(taskItem.value.taskType),
  };

  areas.push(newArea);

  lastAddedAreaIndex = areas.fields.value.length - 1;

  selectLast();
}

const medicalSystemModalOpen = ref<boolean>(false);

function prepareDuplicate(index: number) {
  medicalSystemModalOpen.value = true;
  areaDuplicateIndex.value = index;
  selectLast();
}

const areaDuplicateIndex = ref(1);

function disableScopes(area: TaskItemAreaDto): TaskItemAreaDto {
  return {
    ...area,
    evaluationScopes: area.evaluationScopes?.map((scope) => ({
      ...scope,
      isEnabled: false,
    })),
  };
}

async function duplicateArea(targetSystemId: string) {
  medicalSystemModalOpen.value = false;

  const myArea = areas.fields.value[areaDuplicateIndex.value];
  const medicalSystemSetup = myArea.value.medicalSystemSetup;
  if (!medicalSystemSetup) {
    // Cannot duplicate without a medical system setup
    return;
  }
  const area = toCreateOrUpdateTaskItemAreaDto({
    ...myArea.value,
    medicalSystemSetup,
  });

  const bodyObj: CopyTaskItemAreaDto = {
    targetSystemId,
    taskType: taskItem.value.taskType,
    area,
    $type:
      CopyTaskItemAreaDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoCopyTaskItemAreaDtoPtwPtw100AppZeroApplicationShared,
  };

  const { result, success } = await api.taskItemsApi.copyTaskItemArea({
    copyTaskItemAreaDto: bodyObj,
  });

  const isBCC = taskItem.value.taskType === 'BeamConstancyCheck';

  if (success) {
    areas.push(isBCC ? disableScopes(result) : result);
    selectLast();
    if (selectedAreaIndex.value !== undefined) {
      duplicatedAreaIndices.value = [...duplicatedAreaIndices.value, selectedAreaIndex.value];
    }
  }
}

const allMedicalSystemsAssigned = computed(
  () => areas.fields.value.length === medicalSystems.value?.length && areas.fields.value.length > 0,
);

const addAreasDisabled = computed(
  () => !medicalSystems.value?.length || areas.fields.value.length === medicalSystems.value?.length,
);

const medicalSystemsWithoutUsed = computed(() => {
  const targetSystemEntityIds = areas.fields.value
    .map((area) => {
      return area.value?.medicalSystemSetup?.targetSystemEntityId;
    })
    .filter(Boolean);

  return medicalSystems.value?.filter((item) => {
    return !targetSystemEntityIds.includes(item.id);
  });
});

onMounted(async () => {
  getSystemEntities();
});

const areaNotification = computed<NotificationMessage | undefined>(() => {
  return selectedAreaIndex.value !== undefined &&
    duplicatedAreaIndices.value.includes(selectedAreaIndex.value)
    ? {
        type: 'warning',
        message: t('DuplicatedTargetAreaNotification'),
        closable: true,
      }
    : undefined;
});
</script>

<template>
  <div class="d-flex justify-space-between">
    <EntityScopeSelectionModal
      v-model="medicalSystemModalOpen"
      :title="$t('DuplicateTargetArea')"
      :message="$t('SelectMedicalSystemForTargetAreaText')"
      :notification="{
        type: 'warning',
        message: t('SelectMedicalSystemForTargetAreaNotification'),
        closable: false,
      }"
      :options="medicalSystemsWithoutUsed || []"
      @submit="duplicateArea"
    />

    <Tabs
      v-if="selectedAreaIndex !== undefined"
      :tabs="tabs"
      :modelValue="String(selectedAreaIndex)"
      @select="(_key, index) => changeTab(index)"
    />

    <Tooltip v-if="addAreasDisabled" position="top" :showPointer="true">
      <template #target="{ props: targetProps }">
        <div v-bind="targetProps">
          <SecondaryVBtn
            v-if="mode !== 'details'"
            class="v-btn--size-small"
            :disabled="addAreasDisabled"
            :text="$t('AddTargetArea')"
            :prependIcon="mdiPlus"
            v-bind="targetProps"
            @click="
              () => {
                addNewArea();
              }
            "
          />
        </div>
      </template>
      <template #tooltip>{{
        allMedicalSystemsAssigned
          ? $t('NoFurtherTargetAreasCanBeAdded')
          : $t('RequirementToCreateTaskItemTargetArea')
      }}</template>
    </Tooltip>

    <SecondaryVBtn
      v-else-if="mode !== 'details'"
      class="v-btn--size-small"
      :disabled="addAreasDisabled"
      :text="$t('AddTargetArea')"
      :prependIcon="mdiPlus"
      @click="
        () => {
          addNewArea();
        }
      "
    />
  </div>

  <template v-if="qaEquipmentSystemEntities && medicalSystems && taskItem.taskType">
    <InlineNotification
      v-if="selectedArea?.isReferenced && mode === 'edit'"
      variant="flat"
      class="mt-2"
      :value="{
        type: 'info',
        message: $t('TargetAreaReferencedByJobsMsg'),
      }"
    >
      <template #append>
        <span
          v-if="selectedArea?.isReferenced"
          class="ml-2 linkButton"
          @click.stop="
            () => {
              entityReferences = selectedArea?.isReferencedBy ?? null;
            }
          "
          >{{ $t('Info') }}</span
        >
      </template>
    </InlineNotification>
    <AreaForm
      v-if="selectedAreaIndex !== undefined"
      :key="selectedAreaIndex"
      :areaIndex="selectedAreaIndex"
      :taskType="taskItem.taskType"
      :qaEquipmentSystemEntities="qaEquipmentSystemEntities"
      :medicalSystems="medicalSystems"
      :notification="areaNotification"
    />
  </template>

  <EntityReferencesModal
    :hideRefTable="false"
    :references="entityReferences"
    :title="$t('ReferenceingEntities')"
    @dialogOpen="
      (isOpen: boolean) => {
        if (!isOpen) entityReferences = null;
      }
    "
  >
    <template #description>
      {{ $t('TaskReferencesDescriptionMsg') }}
      <br /><br />
      {{ $t('EntityReferencesInfoMsg') }}
    </template>
  </EntityReferencesModal>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';

:deep(.v-tab__slider) {
  /*
  * no other way to set this
  * @url: https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/components/VTabs/VTab.sass
  */
  border-bottom-left-radius: variables.$tab-slider-size + 1;
  border-bottom-right-radius: variables.$tab-slider-size + 1;
}

.tabs {
  position: relative;
  top: variables.$tab-slider-size;

  .v-btn {
    color: rgb(var(--v-theme-secondary));

    &:hover {
      color: rgb(var(--v-theme-secondary-darken-2));

      &:deep(> .v-btn__overlay) {
        opacity: 0;
      }
    }

    &.v-tab--selected {
      color: rgb(var(--v-theme-on-surface));
    }

    &.v-btn--disabled {
      color: rgb(var(--v-theme-primary-lighten-2));
      opacity: 1;
    }
  }
}
</style>
